<template>
  <div>
    <HrmisCrudModule ref="boardDecisionsList" v-bind="config">
      <template #toolbar-right>
        <button v-if="canManage" class="hrmis-btn hrmis-btn-ghost" @click="openBulkUploadModal">
          <i class="bx bx-upload"></i> Bulk Upload
        </button>
      </template>
    </HrmisCrudModule>

    <div v-if="showBulkUpload" class="bulk-upload-overlay" @click.self="closeBulkUploadModal">
      <div class="bulk-upload-dialog">
        <div class="bulk-upload-header">
          <div>
            <h3>Bulk Upload Board Decisions</h3>
            <p>Use the same columns as the 15th September Board Decisions Tracker Matrix.</p>
          </div>
          <button class="close-btn" type="button" @click="closeBulkUploadModal" aria-label="Close">×</button>
        </div>

        <div class="bulk-upload-body">
          <div class="template-box">
            <strong>Required columns</strong>
            <ul>
              <li>Decision reference</li>
              <li>Board type</li>
              <li>Subject</li>
              <li>Summary</li>
              <li>Decision date</li>
              <li>Implementation status</li>
              <li>Responsible department (HRIS)</li>
              <li>Responsible county officer</li>
            </ul>
          </div>

          <button type="button" class="template-btn" @click="downloadTemplate">Download CSV template</button>

          <div class="file-picker-wrap">
            <label for="boardDecisionFile" class="file-label">Choose file</label>
            <input id="boardDecisionFile" ref="boardDecisionFile" type="file" accept=".csv,.xls,.xlsx" @change="handleFileChange" />
          </div>

          <div v-if="selectedFileName" class="selected-file">
            <i class="bx bx-file"></i> {{ selectedFileName }}
          </div>
        </div>

        <div class="bulk-upload-footer">
          <button type="button" class="secondary-btn" @click="closeBulkUploadModal">Cancel</button>
          <button type="button" class="primary-btn" :disabled="uploading || !selectedFile" @click="uploadBoardDecisions">
            <span v-if="uploading" class="spinner"></span>
            {{ uploading ? "Uploading..." : "Upload" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HrmisCrudModule from "./HrmisCrudModule.vue";
import { authUrl, execute, getRight } from "@/api";

export default {
  name: "ModuleBoardDecisions",
  components: { HrmisCrudModule },
  data() {
    return {
      showBulkUpload: false,
      selectedFile: null,
      selectedFileName: "",
      uploading: false,
      canManage: false,
      config: {
        title: "Board decision tracking",
        subtitle: "PSC / board resolutions and implementation status",
        icon: "⚖️",
        listFunction: "getBoardDecisionList",
        saveFunction: "saveBoardDecision",
        permissionViewCodes: ["BOARD_DECISION_VIEW", "BOARD_DECISION_MANAGE"],
        permissionManageCodes: ["BOARD_DECISION_MANAGE"],
        loadDepartments: true,
        columns: [
          { key: "decision_ref", label: "Decision reference" },
          { key: "board_type", label: "Board type" },
          { key: "subject", label: "Subject", truncate: 40 },
          { key: "decision_date", label: "Decision date" },
          { key: "implementation_status", label: "Implementation status" },
          { key: "responsible_department_hris", label: "Responsible department (HRIS)" },
          { key: "responsible_county_officer", label: "Responsible county officer" }
        ],
        fields: [
          { key: "decision_ref", label: "Decision reference", type: "text" },
          {
            key: "board_type",
            label: "Board type",
            type: "select",
            options: ["PSB", "County", "Other"],
            default: "PSB"
          },
          { key: "subject", label: "Subject", type: "text", required: true },
          { key: "summary", label: "Summary", type: "textarea" },
          { key: "decision_date", label: "Decision date", type: "date" },
          {
            key: "implementation_status",
            label: "Implementation status",
            type: "select",
            options: ["Pending", "In Progress", "Completed", "Overdue"],
            default: "Pending"
          },
          { key: "responsible_department_hris", label: "Responsible department (HRIS)", type: "text" },
          { key: "responsible_county_officer", label: "Responsible county officer", type: "text" }
        ]
      }
    };
  },
  created() {
    this.canManage = getRight("BOARD_DECISION_MANAGE");
  },
  methods: {
    openBulkUploadModal() {
      this.showBulkUpload = true;
    },
    closeBulkUploadModal() {
      this.showBulkUpload = false;
      this.selectedFile = null;
      this.selectedFileName = "";
      if (this.$refs.boardDecisionFile) {
        this.$refs.boardDecisionFile.value = "";
      }
    },
    handleFileChange(event) {
      const file = event?.target?.files?.[0] || null;
      this.selectedFile = file;
      this.selectedFileName = file ? file.name : "";
    },
    downloadTemplate() {
      const rows = [
        [
          "Decision reference",
          "Board type",
          "Subject",
          "Summary",
          "Decision date",
          "Implementation status",
          "Responsible department (HRIS)",
          "Responsible county officer"
        ],
        [
          "MIN 3.1/PSB/15TH SEPT/2026",
          "PSB",
          "Retention of appointment letters for staff A",
          "The Board resolved that the appointment letters shall be retained.",
          "2026-09-15",
          "Pending",
          "Office of the County Secretary",
          "County Secretary"
        ]
      ];
      const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "board_decisions_bulk_upload_template.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    uploadBoardDecisions() {
      if (!this.selectedFile) return;
      this.uploading = true;

      const fd = new FormData();
      fd.append("function", "bulkUploadBoardDecisions");
      fd.append("file", this.selectedFile);

      execute(fd, authUrl)
        .then((res) => {
          const result = res?.data || {};
          if (result.success) {
            alert(result.message || "Board decisions uploaded successfully.");
            this.closeBulkUploadModal();
            if (this.$refs.boardDecisionsList && typeof this.$refs.boardDecisionsList.loadList === "function") {
              this.$refs.boardDecisionsList.loadList();
            }
          } else {
            alert(result.message || "Bulk upload failed.");
          }
        })
        .catch((err) => {
          alert(err?.message || "Upload failed.");
        })
        .finally(() => {
          this.uploading = false;
        });
    }
  }
};
</script>

<style scoped>
.bulk-upload-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
}
.bulk-upload-dialog {
  width: min(620px, 92vw);
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.18);
  overflow: hidden;
}
.bulk-upload-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: linear-gradient(135deg, #059669, #0d9488);
  color: #fff;
  padding: 18px 20px;
}
.bulk-upload-header h3 {
  margin: 0;
  font-size: 1.15rem;
}
.bulk-upload-header p {
  margin: 4px 0 0;
  font-size: 0.8rem;
  opacity: 0.9;
}
.close-btn {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1.5rem;
  line-height: 1;
}
.bulk-upload-body {
  padding: 20px;
}
.template-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 16px;
}
.template-box ul {
  margin: 10px 0 0 18px;
  padding: 0;
  color: #475569;
  font-size: 0.92rem;
}
.template-btn,
.primary-btn,
.secondary-btn {
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
}
.template-btn {
  background: #ecfeff;
  color: #0f766e;
  margin-bottom: 16px;
}
.file-picker-wrap {
  display: grid;
  gap: 8px;
}
.file-label {
  font-size: 0.82rem;
  font-weight: 700;
  color: #334155;
}
input[type="file"] {
  width: 100%;
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff;
}
.selected-file {
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ecfdf5;
  color: #065f46;
  border-radius: 999px;
  padding: 8px 12px;
  font-size: 0.82rem;
}
.bulk-upload-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px 20px;
  border-top: 1px solid #e2e8f0;
}
.secondary-btn {
  background: #f1f5f9;
  color: #334155;
}
.primary-btn {
  background: linear-gradient(135deg, #059669, #0d9488);
  color: #fff;
}
.primary-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255,255,255,0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
